/**
 * Saga-map geometry (pure, no Phaser): level 1 sits at the bottom, the road winds upward.
 * World y grows downward like the screen, so higher levels have more negative y.
 */
import { CHAPTER_SIZE, chapterOf } from "./progress";

export const MAP_W = 720;
/** Vertical distance between two levels on the road. */
export const NODE_GAP = 150;
/** Extra space between chapters for the chapter banner. */
export const CHAPTER_GAP = 170;
/** Horizontal swing of the road around the centre line. */
export const SWING = 190;
export const NODE_R = 46;

export interface Pt {
  x: number;
  y: number;
}

export function nodePoint(level: number, size = CHAPTER_SIZE): Pt {
  const i = Math.max(1, Math.floor(level)) - 1;
  return { x: MAP_W / 2 + SWING * Math.sin(i * 0.85), y: -(i * NODE_GAP + chapterOf(i + 1, size) * CHAPTER_GAP) };
}

/** Where a chapter's banner sits: halfway between its first level and the previous chapter's last. */
export function chapterBannerY(chapter: number, size = CHAPTER_SIZE): number {
  return nodePoint(chapter * size + 1, size).y + (NODE_GAP + CHAPTER_GAP) / 2;
}

/** Vertical extent [top, bottom] of a chapter's ground band; neighbouring bands touch exactly. */
export function chapterBand(chapter: number, size = CHAPTER_SIZE): [number, number] {
  return [chapterBannerY(chapter + 1, size), chapterBannerY(chapter, size)];
}

/** Small deterministic hash → [0, 1) for decoration placement (no Math.random anywhere). */
export function hash01(a: number, b: number): number {
  let h = (Math.imul(a + 0x9e37, 0x85ebca6b) ^ Math.imul(b + 0x7f4a, 0xc2b2ae35)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Road centre-line x at world y (linear between neighbouring levels), used to keep decorations off the road. */
export function roadX(y: number, size = CHAPTER_SIZE): number {
  let prev = nodePoint(1, size);
  if (y >= prev.y) return prev.x;
  for (let n = 2; n < 10000; n++) {
    const p = nodePoint(n, size);
    if (y >= p.y) return prev.x + ((y - prev.y) / (p.y - prev.y)) * (p.x - prev.x);
    prev = p;
  }
  return prev.x;
}
